// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * RESEARCH §3, as data: what each transpile leg rewrites, in order, and what it loses.
 *
 * TWO RULES THE TRANSPILERS INHERIT FROM THIS FILE:
 *
 * 1. **Transpile from the canonical schema, never provider → provider.** In particular never
 *    OpenAI-strict → Gemini: OpenAI strict *mandates* `["T","null"]` for every optional field, and
 *    that is the single shape Gemini `parameters` cannot represent. Every leg below starts at
 *    `canonical`.
 *
 * 2. **Only `SEMANTICS` warrants a warning.** Four categories, not two: `DROPPED`, `REJECTED`
 *    (would 400), `INERT` (accepted and documented as doing nothing), `SEMANTICS` (the meaning
 *    changed). The first three are rows in the lossiness table. The last is a modal.
 */

import type { LossKind, Provenance, TargetId } from '../types.ts';

/** One rewrite the transpiler performs, in the order it performs it. */
export interface Rewrite {
  readonly step: string;
  readonly detail: string;
  /** True when applying this silently would change meaning — the UI must warn before applying. */
  readonly semantic: boolean;
}

/** One keyword's fate on this leg. */
export interface LossRule {
  readonly keyword: string;
  readonly kind: LossKind;
  readonly detail: string;
}

export interface TranspileLeg {
  readonly target: TargetId;
  readonly label: string;
  /** Ordered — a later rewrite may depend on an earlier one having run. */
  readonly rewrites: readonly Rewrite[];
  readonly losses: readonly LossRule[];
  /** Whole-leg caveats the UI must show above the table. */
  readonly caveats: readonly string[];
  readonly provenance: Provenance;
}

const CANONICAL_ONLY =
  'Transpile from the canonical schema, never from another provider\'s output.';

export const LOSSINESS: readonly TranspileLeg[] = [
  // ── canonical → openai-strict ───────────────────────────────────────────────────────────────────
  {
    target: 'openai-strict',
    label: 'canonical → OpenAI strict',
    provenance: 'SDK',
    rewrites: [
      {
        step: 'normalise the root',
        detail:
          'Accept the single-element list ["object"] and normalise it to "object" — that is exactly what the SDK\'s _ensure_strict_root does. Reject a root anyOf and a nullable root ["object","null"].',
        semantic: false,
      },
      {
        step: 'unravel $refs carrying sibling keys',
        detail:
          'Resolved keys first, LOCAL KEYS WIN ({**resolved, **json_schema}), pop the $ref, then re-run the strict pass so chained refs survive. Do not eagerly expand a bare $ref — {"$ref":"#"} is OpenAI\'s own documented recursive example and must terminate. Reject only refs that do not start with "#".',
        semantic: false,
      },
      {
        step: 'flatten single-entry allOf',
        detail: 'A one-element allOf inlines. A multi-entry allOf must be merged by hand or the tool refused.',
        semantic: false,
      },
      { step: 'rewrite oneOf to anyOf', detail: 'Exclusivity is lost — no constrained decoder enforces it.', semantic: true },
      {
        step: 'inject additionalProperties:false at every object level',
        detail:
          'Compare with `!== false`, never truthiness. `additionalProperties:{}` — the MCP/OpenAPI trap — is falsy in JS, so a naive linter passes a schema the API rejects.',
        semantic: false,
      },
      {
        step: 'set required = every key of properties',
        detail: 'Non-negotiable under strict. This alone promotes every optional argument to mandatory.',
        semantic: true,
      },
      {
        step: 'widen previously-optional properties to ["T","null"]',
        detail:
          'THE correction: OpenAI\'s own Agents SDK does only `json_schema["required"] = list(properties.keys())` — it NEVER inserts "null". The nullable-union idiom is human guidance from function-calling.md. Do both, and warn that any repairer doing only the first — including OpenAI\'s own SDK — silently promotes optional args to mandatory. "Absent" and "explicitly null" are different values, and the callee must now handle null.',
        semantic: true,
      },
      {
        step: 'drop `default: null`, report a non-null `default`',
        detail:
          'A null default is noise; a non-null default carries information that vanishes, so it is reported rather than silently dropped.',
        semantic: true,
      },
    ],
    losses: [
      { keyword: 'allOf', kind: 'REJECTED', detail: 'Multi-entry allOf cannot be represented; merge by hand or refuse the tool.' },
      { keyword: 'not', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'if', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'then', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'else', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'dependentRequired', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'dependentSchemas', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'patternProperties', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'propertyNames', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'minProperties', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'maxProperties', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'unevaluatedProperties', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'unevaluatedItems', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'contains', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'minContains', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'maxContains', kind: 'REJECTED', detail: 'Not permitted.' },
      { keyword: 'uniqueItems', kind: 'REJECTED', detail: "In context=('properties', 'ids'), 'uniqueItems' is not permitted." },
      { keyword: 'default', kind: 'DROPPED', detail: "'default' is not permitted. A null default is dropped; a non-null one is reported." },
      { keyword: 'oneOf', kind: 'SEMANTICS', detail: 'Rewritten to anyOf: exclusivity is silently lost.' },
      {
        keyword: 'required',
        kind: 'SEMANTICS',
        detail: 'Every property becomes required; optionality survives only as an explicit null in the type union.',
      },
    ],
    caveats: [
      CANONICAL_ONLY,
      'Zero-arg tools: the canonical form {"type":"object","properties":{},"required":[],"additionalProperties":false} is byte-for-byte the SDK\'s _EMPTY_SCHEMA. Present it as RECOMMENDED, not as the only accepted form — Responses requires the `parameters` key to be present but permits null, and Chat Completions documents that omitting `parameters` defines a function with an empty parameter list. Whether `parameters:{}` 400s is untested.',
      'The subset is validated at request time on Responses regardless of `strict`, so "just turn strict off" is never the fix. One bad tool 400s the entire tools array.',
    ],
  },

  // ── canonical → openai non-strict ───────────────────────────────────────────────────────────────
  {
    target: 'openai-nonstrict-responses',
    label: 'canonical → OpenAI Responses, non-strict',
    provenance: 'FIELD',
    rewrites: [
      {
        step: 'sanitise the keyword set anyway',
        detail:
          'The Responses API rejects oneOf even when strict is omitted. The subset is enforced at request time; only the required/additionalProperties/nullable-union rewrites are skipped.',
        semantic: false,
      },
      { step: 'rewrite oneOf to anyOf', detail: 'Exclusivity is lost.', semantic: true },
    ],
    losses: [
      { keyword: 'oneOf', kind: 'SEMANTICS', detail: 'Rewritten to anyOf; rejected outright if left in place.' },
      { keyword: 'default', kind: 'REJECTED', detail: "'default' is not permitted." },
      { keyword: 'uniqueItems', kind: 'REJECTED', detail: 'Not permitted.' },
    ],
    caveats: [
      CANONICAL_ONLY,
      'The loudest lint rule in the product applies here: the Responses API auto-normalises a schema into strict mode when it can and falls back to non-strict best-effort when it cannot, signalling that only by echoing `strict: false` on the tool. Check what came back.',
    ],
  },
  {
    target: 'openai-nonstrict-chat',
    label: 'canonical → OpenAI Chat Completions, non-strict',
    provenance: 'DOC',
    rewrites: [
      {
        step: 'nest the tool',
        detail:
          'Chat Completions nests: {type:"function", function:{name, description, parameters, strict}}. Responses is flat: {type:"function", name, description, parameters, strict}. Emit two separate artefacts, never one ambiguous tools.openai.json.',
        semantic: false,
      },
    ],
    losses: [],
    caveats: [
      CANONICAL_ONLY,
      'Nothing is enforced on this target, so nothing is lost — and nothing is checked either. Every constraint here is a prompt hint. The moment you set strict:true, the openai-strict column applies in full.',
    ],
  },

  // ── canonical → anthropic-strict ────────────────────────────────────────────────────────────────
  {
    target: 'anthropic-strict',
    label: 'canonical → Anthropic strict',
    provenance: 'DOC',
    rewrites: [
      {
        step: 'keep optional properties as they are',
        detail:
          'Do NOT apply the OpenAI required-everything rewrite here. Optional properties are legal — but every one counts toward the request-wide 24-optional-parameter budget.',
        semantic: false,
      },
      { step: 'set additionalProperties:false on every object', detail: 'It may be set to nothing else.', semantic: false },
      { step: 'lift nested allOf', detail: 'Nested allOf survives; allOf combined with $ref does not.', semantic: false },
      {
        step: 'strip numeric and string bounds',
        detail:
          'minimum, maximum, multipleOf, exclusiveMinimum, exclusiveMaximum, minLength, maxLength and maxItems are all rejected. minItems survives only as 0 or 1.',
        semantic: true,
      },
      {
        step: 'scan every `pattern` for unsupported regex constructs',
        detail:
          'Backreferences, lookahead/lookbehind, \\b/\\B and complex {n,m} ranges are unsupported. A belt full of \\b is legal JSON Schema, legal on OpenAI strict, and a 400 here.',
        semantic: false,
      },
      {
        step: 'refuse recursive schemas',
        detail: 'Recursive schemas are unsupported outright; there is no rewrite that preserves them.',
        semantic: false,
      },
    ],
    losses: [
      { keyword: 'minimum', kind: 'REJECTED', detail: 'A numerical constraint — unsupported under strict.' },
      { keyword: 'maximum', kind: 'REJECTED', detail: 'A numerical constraint — unsupported under strict.' },
      { keyword: 'exclusiveMinimum', kind: 'REJECTED', detail: 'A numerical constraint.' },
      { keyword: 'exclusiveMaximum', kind: 'REJECTED', detail: 'A numerical constraint.' },
      { keyword: 'multipleOf', kind: 'REJECTED', detail: 'A numerical constraint.' },
      { keyword: 'minLength', kind: 'REJECTED', detail: 'A string constraint.' },
      { keyword: 'maxLength', kind: 'REJECTED', detail: 'A string constraint.' },
      { keyword: 'maxItems', kind: 'REJECTED', detail: 'An array constraint beyond minItems 0 or 1.' },
      { keyword: 'minItems', kind: 'REJECTED', detail: 'Survives only with the value 0 or 1.' },
      { keyword: '$ref#root', kind: 'REJECTED', detail: 'Recursive schemas are unsupported; the tool must be refused.' },
      {
        keyword: 'properties',
        kind: 'SEMANTICS',
        detail:
          'Under strict, Anthropic REORDERS object keys — "required properties appear first, followed by optional properties" — regardless of your schema order. Any propertyOrdering carried over from Gemini is not honoured; do not silently preserve the field.',
      },
      {
        keyword: 'enum',
        kind: 'SEMANTICS',
        detail: 'Capitalisation of the generated member is not guaranteed. Compare case-insensitively after parsing.',
      },
      {
        keyword: 'const',
        kind: 'SEMANTICS',
        detail: 'Capitalisation of the generated value is not guaranteed.',
      },
    ],
    caveats: [
      CANONICAL_ONLY,
      'This target KEEPS what OpenAI strict loses — allOf (nested, non-$ref), optional properties, `default`, `const`, the `uri` format — and LOSES what OpenAI keeps: every numeric bound, both length bounds, maxItems, and recursion.',
      'Three belt-wide budgets apply before any of this matters: more than 20 strict tools, more than 24 optional parameters, or more than 16 union-typed parameters across the WHOLE request is a 400, however simple each individual tool looks.',
      'Passing the published limits is not a promise the grammar compiles: beyond them sit an unpublished compiled-grammar-size limit and a 180-second compilation timeout.',
    ],
  },

  // ── canonical → anthropic non-strict ────────────────────────────────────────────────────────────
  {
    target: 'anthropic-nonstrict',
    label: 'canonical → Anthropic, no strict',
    provenance: 'DOC',
    rewrites: [
      {
        step: 'rename parameters to input_schema',
        detail: 'Anthropic has no `parameters` key; the schema rides in `input_schema`.',
        semantic: false,
      },
      {
        step: 'lift any top-level oneOf/allOf/anyOf',
        detail:
          'Rejected at the TOP LEVEL of input_schema only: "input_schema does not support oneOf, allOf, or anyOf at the top level". Nested composition is untouched.',
        semantic: false,
      },
    ],
    losses: [
      { keyword: 'oneOf', kind: 'REJECTED', detail: 'At the top level of input_schema.' },
      { keyword: 'allOf', kind: 'REJECTED', detail: 'At the top level of input_schema.' },
      { keyword: 'anyOf', kind: 'REJECTED', detail: 'At the top level of input_schema.' },
    ],
    caveats: [
      CANONICAL_ONLY,
      'Nothing else is enforced, and no cap is published for non-strict tool count or total schema size. Keep that honest language for non-strict only — the strict path publishes three hard numbers.',
    ],
  },

  // ── canonical → anthropic via the official SDK ──────────────────────────────────────────────────
  {
    target: 'anthropic-sdk',
    label: 'canonical → Anthropic via the official SDK',
    provenance: 'DOC',
    rewrites: [
      {
        step: 'the SDK removes unsupported constraints',
        detail: 'Its own words: "Remove unsupported constraints (for example, minimum, maximum, minLength, maxLength)".',
        semantic: true,
      },
      {
        step: 'the SDK rewrites your descriptions',
        detail: '"Update descriptions with constraint info" — the constraint survives as prose the model may or may not honour.',
        semantic: true,
      },
      { step: 'the SDK adds additionalProperties:false to all objects', detail: 'Whether you wrote it or not.', semantic: true },
      { step: 'the SDK filters string formats to the supported list', detail: 'An out-of-list format silently disappears.', semantic: false },
      {
        step: 'the SDK validates the response against your ORIGINAL schema',
        detail:
          'So a violation of a stripped constraint surfaces client-side, after generation, as a validation failure rather than as a 400.',
        semantic: false,
      },
    ],
    losses: [
      { keyword: 'minimum', kind: 'INERT', detail: 'Stripped from the wire, appended to the description. On the wire it is a prompt hint.' },
      { keyword: 'maximum', kind: 'INERT', detail: 'Stripped from the wire, appended to the description.' },
      { keyword: 'multipleOf', kind: 'INERT', detail: 'Stripped from the wire, appended to the description.' },
      { keyword: 'minLength', kind: 'INERT', detail: 'Stripped from the wire, appended to the description.' },
      { keyword: 'maxLength', kind: 'INERT', detail: 'Stripped from the wire, appended to the description.' },
      {
        keyword: 'additionalProperties',
        kind: 'SEMANTICS',
        detail: 'Forced to false on every object, so a deliberately permissive object silently becomes a closed one.',
      },
    ],
    caveats: [
      CANONICAL_ONLY,
      'This is the genuine documented accept-then-drop of the whole problem space: the wire schema differs from the one you wrote, and five constraints survive only as prose plus a client-side re-check.',
      'The SDK\'s strip list is introduced with "for example", so it is not exhaustive. Whether exclusiveMinimum, exclusiveMaximum or maxItems are also stripped is undocumented.',
    ],
  },

  // ── canonical → gemini-parameters (the lossiest leg) ────────────────────────────────────────────
  {
    target: 'gemini-parameters',
    label: 'canonical → Gemini parameters',
    provenance: 'DOC',
    rewrites: [
      {
        step: 'inline every $ref and $defs',
        detail: 'And refuse the tool if the references are recursive — there is no way to inline a cycle.',
        semantic: false,
      },
      { step: 'rewrite oneOf to anyOf', detail: 'Exclusive becomes inclusive.', semantic: true },
      { step: 'merge or refuse allOf', detail: 'There is no allOf field; a merge that is not obviously safe must refuse.', semantic: false },
      { step: 'rewrite const to a one-element enum', detail: 'There is no const field.', semantic: false },
      {
        step: 'rewrite exclusiveMinimum/exclusiveMaximum to minimum/maximum',
        detail: 'Lossy — the boundary value becomes legal. Must be declared, never applied silently.',
        semantic: true,
      },
      {
        step: 'rewrite type arrays to nullable or anyOf',
        detail:
          '{"type":["string","null"]} becomes {"type":"STRING","nullable":true} or anyOf:[{type:STRING},{type:NULL}]. `nullable` is a first-class boolean field here.',
        semantic: true,
      },
      {
        step: 'stringify non-string enum members',
        detail:
          '{"type":"integer","enum":[1,2,3]} becomes {type:INTEGER, format:"enum", enum:["1","2","3"]} and comes back as strings. This is a TYPE change, not a formatting change, and it is specific to this one leg — OpenAI and Anthropic both preserve enum member type.',
        semantic: true,
      },
      {
        step: 'emit type as an UPPERCASE proto enum name',
        detail: 'STRING / NUMBER / INTEGER / BOOLEAN / ARRAY / OBJECT / NULL. Whether the REST endpoint also tolerates lowercase is untested — do not claim either way.',
        semantic: false,
      },
      {
        step: 'emit int64 fields as plain numbers',
        detail:
          'minItems, maxItems, minLength, maxLength, minProperties and maxProperties are declared format:int64, type:string — but ProtoJSON accepts "either numbers or strings". Emit numbers, expect strings back, and treat "2" and 2 as equal when diffing, or every export carries a gratuitous diff.',
        semantic: false,
      },
      {
        step: 'strip every key the Schema message does not have',
        detail:
          '$schema, $ref, $defs, definitions, additionalProperties, allOf, not, if/then/else, uniqueItems, multipleOf, exclusiveMinimum, exclusiveMaximum, patternProperties, propertyNames, unevaluated*, contains*, prefixItems, readOnly, writeOnly, deprecated, $comment. Anything left over returns `Unknown name … Cannot find field`.',
        semantic: false,
      },
    ],
    losses: [
      { keyword: 'additionalProperties', kind: 'REJECTED', detail: 'The field does not exist. THE headline: the single most-Googled repair in this problem space — adding additionalProperties:false for OpenAI or Anthropic strict — is itself a Gemini-breaking change.' },
      { keyword: '$ref', kind: 'REJECTED', detail: 'Must be inlined; a recursive ref means the tool cannot be represented at all.' },
      { keyword: '$defs', kind: 'REJECTED', detail: 'Must be inlined.' },
      { keyword: 'definitions', kind: 'REJECTED', detail: 'Must be inlined.' },
      { keyword: '$schema', kind: 'REJECTED', detail: 'Unknown name "$schema" at \'tools[0].function_declarations[0].parameters\'' },
      { keyword: 'allOf', kind: 'REJECTED', detail: 'No field; merge by hand or refuse.' },
      { keyword: 'not', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'if', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'then', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'else', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'uniqueItems', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'multipleOf', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'patternProperties', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'propertyNames', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'unevaluatedProperties', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'unevaluatedItems', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'contains', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'minContains', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'maxContains', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'prefixItems', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'readOnly', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'writeOnly', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'deprecated', kind: 'REJECTED', detail: 'No field.' },
      { keyword: '$comment', kind: 'REJECTED', detail: 'No field.' },
      { keyword: 'examples', kind: 'REJECTED', detail: 'No field — Gemini\'s is the OpenAPI singular `example`.' },
      { keyword: 'default', kind: 'INERT', detail: 'Accepted and documented as ignored.' },
      { keyword: 'format', kind: 'INERT', detail: '"Any value is allowed, but most do not trigger any special functionality."' },
      { keyword: 'oneOf', kind: 'SEMANTICS', detail: 'Becomes anyOf — exclusive becomes inclusive.' },
      { keyword: 'exclusiveMinimum', kind: 'SEMANTICS', detail: 'Becomes an inclusive minimum: the boundary value becomes legal.' },
      { keyword: 'exclusiveMaximum', kind: 'SEMANTICS', detail: 'Becomes an inclusive maximum: the boundary value becomes legal.' },
      { keyword: 'enum', kind: 'SEMANTICS', detail: 'Integer and boolean members are stringified and come back as strings — a type change.' },
      { keyword: 'type#union', kind: 'SEMANTICS', detail: 'Becomes nullable:true or an anyOf — a representation change, not a rename.' },
    ],
    caveats: [
      CANONICAL_ONLY + ' Never OpenAI-strict → Gemini: the ["T","null"] shape strict mandates is the one shape this target cannot express.',
      'The 22-field Schema message is identical in v1beta and in v1 (GA), so this leg is the same on both.',
      'That additionalProperties:false is required by two providers and non-existent on the third is the argument for a matrix view rather than a linear linter.',
    ],
  },

  // ── canonical → gemini-jsonschema ───────────────────────────────────────────────────────────────
  {
    target: 'gemini-jsonschema',
    label: 'canonical → Gemini parametersJsonSchema',
    provenance: 'INFERRED',
    rewrites: [
      {
        step: 'emit as a second, separate artefact',
        detail:
          'parametersJsonSchema and parameters are MUTUALLY EXCLUSIVE — setting both on one FunctionDeclaration is a 400. "Emit both" can only ever mean two files.',
        semantic: false,
      },
      {
        step: 'check the API version first',
        detail:
          'The field is absent from Gemini API v1 (GA): the GA FunctionDeclaration carries only {description, name, parameters, response}. GA callers must take the lossy parameters path.',
        semantic: false,
      },
    ],
    losses: [],
    caveats: [
      CANONICAL_ONLY,
      'EXPECTED, UNVERIFIED — Google publishes no supported-keyword list for parametersJsonSchema. The field\'s own description says only that it describes the parameters in JSON Schema format, must describe an object, and is mutually exclusive with `parameters`. The richer $id/$defs/$ref/$anchor/prefixItems/oneOf-as-anyOf list belongs to GenerationConfig\'s response JSON-schema field — a different message, marked deprecated:true. Telling a user their recursive $ref is safe here on the strength of a list attached to another message type is exactly the confident-and-wrong this tool exists to prevent.',
      'This leg reports no losses not because none exist, but because nobody has published enough to name them. Read that as "unknown", never as "lossless".',
    ],
  },

  // ── canonical → vertex-parameters ───────────────────────────────────────────────────────────────
  {
    target: 'vertex-parameters',
    label: 'canonical → Vertex AI parameters',
    provenance: 'DOC',
    rewrites: [
      {
        step: 'apply the Gemini parameters leg, minus three strips',
        detail:
          'Vertex\'s Schema has 25 fields to the Gemini API\'s 22 — mechanically derived from the two discovery documents. `$ref` becomes `ref`, `$defs` becomes `defs` (root level only), and `additionalProperties` survives.',
        semantic: false,
      },
      {
        step: 'check the tool name\'s first character',
        detail: 'Vertex adds a rule the Gemini API does not state: the name must start with a letter or an underscore.',
        semantic: false,
      },
    ],
    losses: [
      { keyword: '$ref', kind: 'SEMANTICS', detail: 'Renamed to `ref` — the key changes, the meaning does not, and it will not round-trip without the mapping.' },
      { keyword: '$defs', kind: 'SEMANTICS', detail: 'Renamed to `defs`, and accepted at the root level only.' },
      { keyword: 'const', kind: 'REJECTED', detail: 'No field — rewrite to a one-element enum, as on the Gemini API.' },
      { keyword: 'oneOf', kind: 'SEMANTICS', detail: 'No field — becomes anyOf, and exclusivity is lost.' },
      { keyword: 'enum', kind: 'SEMANTICS', detail: 'Same string-only handling as the Gemini API: non-string members are stringified.' },
      { keyword: 'type#union', kind: 'SEMANTICS', detail: 'Becomes nullable:true or an anyOf.' },
    ],
    caveats: [
      CANONICAL_ONLY,
      'RESEARCH §3 does not cover this leg; it is derived from the difference between the two discovery documents plus the Gemini leg above. Treat the derived parts as INFERRED.',
      'Vertex\'s discovery document calls Schema.type "Optional." where the Gemini API\'s calls it "Required." — the two APIs disagree about their own shared-looking message.',
    ],
  },

  // ── canonical → GBNF ────────────────────────────────────────────────────────────────────────────
  {
    target: 'gbnf-llamacpp',
    label: 'canonical → GBNF (llama.cpp)',
    provenance: 'SRC',
    rewrites: [
      {
        step: 'compute the drop set per node',
        detail:
          'Half the drops are CONDITIONAL on sibling keywords, so there is no fixed list: minLength/maxLength survive only on a bare string, minimum/maximum only on an exact integer, prefixItems only when items is absent. That computation is the product.',
        semantic: false,
      },
      {
        step: 'lock key order to the declaration order of `properties`',
        detail: 'Not the order of `required`. This surprises everyone.',
        semantic: true,
      },
      {
        step: 'refuse external and unanchored constructs',
        detail:
          'A remote $ref is a hard error — llama.cpp\'s own fetcher is a stub, and its CLI help tells you to use the Python script instead. An unanchored `pattern` is a hard error too.',
        semantic: false,
      },
    ],
    losses: [
      { keyword: 'description', kind: 'DROPPED', detail: 'A grammar cannot enforce prose.' },
      { keyword: 'default', kind: 'DROPPED', detail: 'No effect on the grammar.' },
      { keyword: 'multipleOf', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'uniqueItems', kind: 'DROPPED', detail: 'Zero occurrences in the converter source.' },
      { keyword: 'patternProperties', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'propertyNames', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'minProperties', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'maxProperties', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'unevaluatedProperties', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'unevaluatedItems', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'contains', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'minContains', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'maxContains', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'not', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'if', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'then', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'else', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'dependentRequired', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'dependentSchemas', kind: 'DROPPED', detail: 'Not implemented.' },
      { keyword: 'nullable', kind: 'DROPPED', detail: 'An OpenAPI-ism the converter ignores.' },
      { keyword: '$schema', kind: 'DROPPED', detail: 'Ignored.' },
      { keyword: '$ref#siblings', kind: 'DROPPED', detail: 'Sibling keys next to a $ref are silently ignored — draft-07 behaviour.' },
      { keyword: 'required#ghost', kind: 'DROPPED', detail: 'A required entry with no matching property silently vanishes.' },
      { keyword: '$ref#external', kind: 'REJECTED', detail: 'Hard error: the fetcher is a stub.' },
      { keyword: 'minLength', kind: 'DROPPED', detail: 'CONDITIONAL — dropped unless type is exactly "string" and there is no sibling pattern/enum/const/supported format.' },
      { keyword: 'maxLength', kind: 'DROPPED', detail: 'CONDITIONAL — same guard as minLength.' },
      { keyword: 'minimum', kind: 'DROPPED', detail: 'CONDITIONAL — dropped unless type is exactly "integer". On "number" or an untyped node it vanishes, with a literal // TODO in the source.' },
      { keyword: 'maximum', kind: 'DROPPED', detail: 'CONDITIONAL — same guard as minimum.' },
      { keyword: 'minItems', kind: 'DROPPED', detail: 'CONDITIONAL — dropped when the prefixItems tuple form is in play.' },
      { keyword: 'maxItems', kind: 'DROPPED', detail: 'CONDITIONAL — same guard as minItems.' },
      { keyword: 'prefixItems', kind: 'DROPPED', detail: 'CONDITIONAL — honoured only when `items` is absent; a 2020-12 tuple carrying both loses the entire tuple.' },
      { keyword: 'allOf', kind: 'DROPPED', detail: 'PARTIAL — properties/enum/$ref components are shallow-merged, any other component is silently discarded.' },
      { keyword: 'format', kind: 'DROPPED', detail: 'Every value except date, time, date-time and uuid drops to a bare string.' },
      { keyword: 'oneOf', kind: 'SEMANTICS', detail: 'Compiled as anyOf — exclusivity is never enforced.' },
      { keyword: 'properties', kind: 'SEMANTICS', detail: 'Key order is hard-locked to the declaration order of `properties`.' },
      { keyword: 'type', kind: 'SEMANTICS', detail: 'A generated number is capped at 16 integral and 16 fractional digits, and an unbounded integer side at 16 digits — so minimum:0 does not admit every non-negative integer.' },
    ],
    caveats: [
      CANONICAL_ONLY,
      'llama.cpp accumulates every hard error and throws once. Report them all: a belt with four bad patterns should report four, not the first.',
      'llama.cpp\'s warnings go to stderr and vanish in a browser unless the UI surfaces them.',
      '--grammar-file parity must NOT be claimed: it installs a non-lazy user grammar, whereas llama-server\'s own tool grammars are lazy, trigger-gated and wrapped in template-specific markers. Feeding a static tools.gbnf that way forces a tool call from token 0 and forbids prose.',
    ],
  },
  {
    target: 'gbnf-xgrammar',
    label: 'canonical → GBNF (xgrammar / vLLM)',
    provenance: 'SRC',
    rewrites: [
      {
        step: 'rewrite the `space` rule',
        detail:
          'llama.cpp emits a bare leading `|`, and xgrammar\'s parser demands at least one element per sequence: "EBNF parser error at line N, column 11: Expect element, but got |". llama.cpp\'s own output does not parse in xgrammar.',
        semantic: false,
      },
      {
        step: 'mangle rule names that start with a digit',
        detail:
          'A property literally named "1st" yields the rule `1st-kv`, and xgrammar\'s LEXER rejects it: "EBNF lexer error: The rule name should be at the beginning of the line". Two bugs, not one.',
        semantic: false,
      },
    ],
    losses: [],
    caveats: [
      CANONICAL_ONLY,
      'Keyword handling is identical to llama.cpp — see that leg for the drop set. Only the emission differs.',
      'In vLLM the user never sees either message: validate_xgrammar_grammar swallows it and raises a bare ValueError("Invalid grammar specification.").',
      'vLLM\'s guided_json takes a JSON Schema, never a grammar, and guided_* was removed in v0.12.0. The current call is extra_body={"structured_outputs": {"grammar": <gbnf>}}, and only the xgrammar backend understands GBNF.',
    ],
  },
];

const LEG_BY_TARGET: ReadonlyMap<TargetId, TranspileLeg> = new Map(LOSSINESS.map((l) => [l.target, l]));

export function legFor(target: TargetId): TranspileLeg | undefined {
  return LEG_BY_TARGET.get(target);
}

/** The only losses that warrant a modal. Everything else is a table row. */
export function semanticLosses(target: TargetId): readonly LossRule[] {
  return (LEG_BY_TARGET.get(target)?.losses ?? []).filter((l) => l.kind === 'SEMANTICS');
}

/**
 * Round-tripping is not identity, and the report must say so. An OpenAI optional property becomes
 * ["string","null"] and cannot be mechanically restored to "absent-allowed"; a Gemini integer enum
 * returns as strings; a oneOf that survived to Gemini returns as anyOf.
 */
export const ROUND_TRIP_WARNINGS: readonly string[] = [
  'An OpenAI optional property becomes ["string","null"] and cannot be mechanically restored to "absent-allowed".',
  'A Gemini integer enum returns as strings.',
  'A oneOf that survived to Gemini returns as anyOf.',
];

/**
 * The five call/result data-model mismatches a reconciler must handle. These are not field renames,
 * and a round trip that treats them as such turns a structured failure into an indistinguishable
 * success payload.
 */
export const CALL_MODEL_MISMATCHES: readonly { readonly topic: string; readonly detail: string }[] = [
  {
    topic: 'No tool/function role on Anthropic',
    detail:
      'tool_use and tool_result are content blocks inside ordinary user/assistant messages. tool_result blocks must come FIRST in the user message\'s content array and must immediately follow their tool_use.',
  },
  {
    topic: 'Roles',
    detail: 'Gemini has only `user` and `model`; a functionResponse part rides in a user turn.',
  },
  {
    topic: 'Argument encoding',
    detail:
      'OpenAI Chat Completions `arguments` is a JSON STRING; Anthropic `input` and Gemini `args` are objects. A lossless round trip must preserve the original string bytes or it silently reformats every payload.',
  },
  {
    topic: 'Linkage fields',
    detail:
      'OpenAI Responses `call_id` (separate from the item `id`) · Chat Completions `tool_call_id` · Anthropic `tool_use_id` (ids prefixed toolu_; server_tool_use uses srvtoolu_ and must NEVER receive a tool_result) · Gemini `id` on both sides, and optional on the call.',
  },
  {
    topic: 'Error signalling',
    detail:
      'Anthropic has a first-class is_error:true on tool_result; OpenAI\'s function_call_output carries only an opaque output string; Gemini conveys errors by convention as an "error" key inside the free-form response object. Preserve the flag out-of-band or a structured failure becomes an indistinguishable success.',
  },
];

/** Renaming a tool to fit a target charset breaks call-id linkage unless this artefact ships too. */
export const NAME_MAP_ARTEFACT = 'tool-name-map.csv';
